/**
 * Time Travel Helpers
 * Functions for managing time travel state and UI
 */

import { HexEvents, createHexEvent } from './hex-events.js';
import { addDaysToDate, formatGameDate, calculateDaysElapsed, DEFAULT_GAME_DATE } from './calendar.js';
import { findLastEventBeforeDate, rebuildState } from './event-sourcing.js';
import { refreshMapDisplay } from '../components/hex-map.js';

/**
 * Update read-only state for all editing controls
 * @param {boolean} isReadOnly - Whether editing should be disabled
 */
export function updateReadOnlyState(isReadOnly) {
  // Store read-only state globally so components can check it
  window.currentSession.isReadOnly = isReadOnly;

  // Dispatch event for components to update their state
  document.dispatchEvent(createHexEvent(HexEvents.READONLY_STATE_CHANGED, {
    isReadOnly
  }));
}

/**
 * Set the time travel day (filters historical state)
 * @param {number} day - The day to travel to
 * @param {boolean} broadcast - Whether to broadcast to Player View
 * @param {BroadcastChannel} gmChannel - GM-Player sync channel
 * @param {boolean} isPlayerView - Whether this is player view
 */
export async function setTimeTravelDay(day, broadcast = true, gmChannel, isPlayerView) {
  const currentDaysElapsed = window.currentSession.calendar?.daysElapsed || 1;
  const isHistorical = day < currentDaysElapsed;
  const timeDisplay = document.getElementById('time-display');
  const historicalIndicator = document.getElementById('historical-indicator');

  // Update display
  if (timeDisplay) {
    timeDisplay.textContent = `Day ${day}`;
  }

  // Calculate the date for this day
  const startDate = window.currentSession.calendar?.startDate || DEFAULT_GAME_DATE;
  const targetDate = addDaysToDate(startDate, day - 1);

  // Show/hide historical indicator with date
  const jumpToPresentBtn = document.getElementById('jump-to-present-btn');
  if (historicalIndicator) {
    if (isHistorical) {
      const dateStr = formatGameDate(targetDate);
      historicalIndicator.textContent = `Viewing: ${dateStr} (Historical)`;
      historicalIndicator.style.display = 'inline';

      // Show Present button when in historical mode
      if (jumpToPresentBtn) {
        jumpToPresentBtn.style.display = 'inline-block';
      }
    } else {
      historicalIndicator.style.display = 'none';

      // Hide Present button when at present
      if (jumpToPresentBtn) {
        jumpToPresentBtn.style.display = 'none';
      }
    }
  }

  // Require event sourcing system
  if (!window.currentSession?.history) {
    console.error('Event sourcing not initialized - time travel unavailable');
    return;
  }

  if (isHistorical) {
    // Find the last event before or on this date
    const eventId = findLastEventBeforeDate(window.currentSession.history, targetDate);

    // Set time travel event ID
    window.currentSession.timeTravelEventId = eventId || 0;

    // Rebuild state up to that event
    rebuildState(window.currentSession, window.currentSession.timeTravelEventId);
  } else {
    // Return to present - rebuild with all events
    window.currentSession.timeTravelEventId = null;
    rebuildState(window.currentSession, window.currentSession.currentEventId);
  }

  // Update hex-map component's session reference
  const hexMap = document.getElementById('main-map');
  if (hexMap) {
    hexMap.session = window.currentSession;
  }

  // Update read-only state for controls
  updateReadOnlyState(isHistorical);

  // Refresh the map to show historical state
  await refreshMapDisplay();

  // Broadcast time travel change to Player View (only from GM view)
  if (broadcast && !isPlayerView && gmChannel) {
    gmChannel.postMessage({
      type: 'time-travel-update',
      day: day
    });
  }
}

/**
 * Jump to present (exit time travel mode)
 */
export function jumpToPresent() {
  const currentDaysElapsed = window.currentSession.calendar?.daysElapsed || 1;
  const timeSlider = document.getElementById('time-slider');

  if (timeSlider) {
    timeSlider.value = currentDaysElapsed;
    const setTimeTravelDayFn = window.__setTimeTravelDay__;
    if (setTimeTravelDayFn) {
      setTimeTravelDayFn(currentDaysElapsed);
    }
    // Also dispatch input event to update UI components (time display, etc.)
    timeSlider.dispatchEvent(new Event('input', { bubbles: true }));
  }
}

/**
 * Populate bookmark dropdown for time travel
 */
export function populateBookmarkDropdown() {
  const bookmarkJump = document.getElementById('bookmark-jump');
  if (!bookmarkJump || !window.currentSession) return;

  const bookmarks = window.currentSession.calendar?.bookmarks || [];
  const startDate = window.currentSession.calendar?.campaignStartDate || DEFAULT_GAME_DATE;

  // Clear existing options except the placeholder
  bookmarkJump.innerHTML = '<option value="">Jump to Bookmark...</option>';

  if (bookmarks.length === 0) {
    const option = document.createElement('option');
    option.value = '';
    option.textContent = 'No bookmarks yet';
    option.disabled = true;
    bookmarkJump.appendChild(option);
    return;
  }

  // Sort bookmarks by date (earliest first)
  const sortedBookmarks = [...bookmarks].sort((a, b) => {
    const aDays = calculateDaysElapsed(startDate, a.gameDate);
    const bDays = calculateDaysElapsed(startDate, b.gameDate);
    return aDays - bDays;
  });

  // Add bookmark options
  sortedBookmarks.forEach(bookmark => {
    const day = calculateDaysElapsed(startDate, bookmark.gameDate);
    const option = document.createElement('option');
    option.value = day;
    option.textContent = `${bookmark.title} (Day ${day})`;
    bookmarkJump.appendChild(option);
  });
}

/**
 * Wire up bookmark jump dropdown handler
 * @param {Function} setTimeTravelDayFn - Function to set time travel day
 */
export function wireBookmarkJump(setTimeTravelDayFn) {
  const bookmarkJump = document.getElementById('bookmark-jump');
  const timeSlider = document.getElementById('time-slider');

  if (bookmarkJump) {
    bookmarkJump.addEventListener('change', (e) => {
      const day = parseInt(e.target.value);
      if (!isNaN(day) && day > 0) {
        // Update slider
        if (timeSlider) {
          timeSlider.value = day;
        }
        // Jump to selected day
        setTimeTravelDayFn(day);
      }
      // Reset dropdown to placeholder
      e.target.value = '';
    });
  }
}
